import { useCallback, useState } from 'react'

export type FormStatus = 'idle' | 'loading' | 'success' | 'error'

interface UseFormSubmissionOptions<TData> {
  submit: (data: TData) => Promise<void>
  onSuccess?: () => void
  onError?: (message: string) => void
}

export function useFormSubmission<TData>({
  submit,
  onSuccess,
  onError,
}: UseFormSubmissionOptions<TData>): {
  status: FormStatus
  errorMessage: string | null
  submit: (data: TData) => Promise<void>
  reset: () => void
} {
  const [status, setStatus] = useState<FormStatus>('idle')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const reset = useCallback(() => {
    setStatus('idle')
    setErrorMessage(null)
  }, [])

  const handleSubmit = useCallback(
    async (data: TData) => {
      setStatus('loading')
      setErrorMessage(null)

      try {
        await submit(data)
        setStatus('success')
        onSuccess?.()
      } catch (error) {
        const message = error instanceof Error ? error.message : 'Something went wrong'
        setStatus('error')
        setErrorMessage(message)
        onError?.(message)
      }
    },
    [submit, onSuccess, onError]
  )

  return { status, errorMessage, submit: handleSubmit, reset }
}
